import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useNavigate, useSearchParams } from 'react-router-dom';

import logo from '../assets/logo_no_bg.png';
import { getMe, verifyEmail } from '../api/auth';
import { AuthContext } from '../context/authContext';
import { useProject } from '../context/ProjectContext';
import { normalizeUserFacingError } from '../utils/normalizeUserFacingError';
import ActionButton from '../components/common/ActionButton';

type VerifyState = 'loading' | 'success' | 'error';

export default function VerifyEmailResultPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const { setCurrentProject } = useProject();
  const [state, setState] = useState<VerifyState>('loading');
  const [error, setError] = useState('');
  const verificationStartedTokenRef = useRef<string | null>(null);
  const verificationSucceededRef = useRef(false);
  const token = searchParams.get('token') || '';

  useEffect(() => {
    void (async () => {
      if (!token) {
        setError('Verification link is missing a token.');
        setState('error');
        return;
      }

      if (verificationStartedTokenRef.current === token) {
        return;
      }

      verificationStartedTokenRef.current = token;
      verificationSucceededRef.current = false;
      setError('');
      setState('loading');

      try {
        const response = await verifyEmail(token);
        let profile = null;

        try {
          profile = await getMe();
        } catch (profileError) {
          console.warn('[VERIFY EMAIL] Failed to load /auth/me', profileError);
        }

        auth?.setAuthData({
          userId: response.user.id,
          username: response.user.username,
          email: response.user.email,
          fullName: profile?.full_name || response.user.full_name,
          organizationId: response.organization.id,
          organizationName: profile?.organization_name || response.organization.name,
          organizationSlug: profile?.organization_slug || response.organization.slug,
          isAdmin: Boolean(profile?.is_admin ?? profile?.admin ?? response.admin),
          isInternalAdmin: profile?.is_internal_admin || false,
          emailVerified: Boolean(profile?.email_verified ?? response.user.email_verified),
        });
        setCurrentProject(null);
        window.history.replaceState({}, document.title, '/verify-email');
        verificationSucceededRef.current = true;
        setState('success');
      } catch (verifyError: any) {
        if (verificationSucceededRef.current) {
          return;
        }
        setError(
          normalizeUserFacingError(
            verifyError,
            'Unable to verify email. The link may have expired.'
          )
        );
        setState('error');
      }
    })();
  }, [auth, token, setCurrentProject]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        background: 'linear-gradient(135deg, #0A0E14 0%, #13171F 100%)',
      }}
    >
      <Paper
        elevation={24}
        sx={{
          width: '100%',
          maxWidth: 480,
          p: { xs: 3, sm: 4 },
          borderRadius: 4,
          background: 'rgba(19, 23, 31, 0.95)',
          border: '1px solid rgba(57, 255, 20, 0.22)',
        }}
      >
        <Stack spacing={3} alignItems="stretch">
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ width: 52, height: 52 }}>
              <img
                src={logo}
                alt="Quantik Mind logo"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Email verification
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Confirming your account.
              </Typography>
            </Box>
          </Stack>

          {state === 'loading' && (
            <Stack direction="row" spacing={2} alignItems="center">
              <CircularProgress size={24} />
              <Typography>Verifying email...</Typography>
            </Stack>
          )}

          {state === 'success' && (
            <>
              <Alert severity="success" icon={<CheckCircleIcon />}>
                Email verified. You can continue onboarding.
              </Alert>
              <ActionButton
                onClick={() => navigate('/onboarding')}
              >
                Continue to onboarding
              </ActionButton>
            </>
          )}

          {state === 'error' && (
            <>
              <Alert severity="error" icon={<ErrorOutlineIcon />}>
                {error}
              </Alert>
              <ActionButton
                onClick={() => navigate('/verify-email/check')}
              >
                Request a new link
              </ActionButton>
            </>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
